import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeader } from 'helpers/auth';
import {
  Activity,
  Wifi,
  WifiOff,
  Clock,
  TrendingUp,
  User,
  Building2,
  MapPin,
  Calendar,
  RefreshCw,
  ChevronRight,
  Zap,
  Radio,
  Database,
  Info,
  X,
  BarChart3,
  Play,
  Square,
  Timer
} from 'lucide-react';

// ── Design tokens ──────────────────────────────────────────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  surf2:   'var(--nd-surface-2)',
  surf3:   'var(--nd-surface-2)',
  border:  'var(--nd-w08)',
  bord2:   'var(--nd-w10)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx35)',
  faint:   'var(--nd-w10)',
  blue:    '#3B82F6',
  teal:    '#22D3A5',
  amber:   '#FBBF24',
  red:     '#F87171',
  purple:  '#A78BFA',
  green:   '#34D399',
  cyan:    '#22D3EE',
  pink:    '#F472B6',
  font:    "'DM Sans', sans-serif",
  mono:    "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin   { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulse  { 0%,100%{opacity:1} 50%{opacity:0.5} }

  .nt-stat-card { transition: transform 0.2s, box-shadow 0.2s; animation: fadeUp 0.35s ease both; }
  .nt-stat-card:hover { transform: translateY(-3px); box-shadow: 0 14px 32px rgba(0,0,0,0.45) !important; }

  .nt-tab {
    flex: 1; padding: 10px 20px; border: none; border-radius: 9px;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 7px;
    transition: all 0.18s;
  }
  .nt-tab-inactive { background: transparent; color: var(--nd-tx30); }
  .nt-tab-inactive:hover { background: var(--nd-w05); color: var(--nd-tx55); }
  .nt-tab-active   { background: linear-gradient(135deg,#3B82F6,#22D3A5); color: var(--nd-text-strong); box-shadow: 0 4px 14px rgba(59,130,246,0.30); }

  .nt-th {
    padding: 11px 14px; text-align: left;
    font-size: 9px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--nd-tx30); font-family: 'DM Sans', sans-serif;
    border-bottom: 1px solid var(--nd-w08); white-space: nowrap;
  }
  .nt-th-right { text-align: right; }

  .nt-tr { border-bottom: 1px solid var(--nd-w05); transition: background 0.15s; }
  .nt-tr:last-child { border-bottom: none; }
  .nt-tr:hover { background: var(--nd-w03) !important; }

  .nt-td { padding: 13px 14px; vertical-align: middle; }
  .nt-td-right { padding: 13px 14px; vertical-align: middle; text-align: right; }

  .nt-refresh-btn {
    display: flex; align-items: center; gap: 7px;
    padding: 8px 16px; border: 1px solid var(--nd-w12); border-radius: 9px;
    background: var(--nd-w05); color: var(--nd-tx70);
    font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 700;
    cursor: pointer; transition: all 0.18s;
  }
  .nt-refresh-btn:hover:not(:disabled) { background: var(--nd-w10); color: var(--nd-text); border-color: var(--nd-tx22); }
  .nt-refresh-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--nd-w10); border-radius: 4px; }
`;

// ── Activity badge colours ─────────────────────────────────────────
const getActivityCfg = (type) => {
  const map = {
    mopping:  { color: '#60A5FA', bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.28)'  },
    brushing: { color: '#F472B6', bg: 'rgba(244,114,182,0.12)', border: 'rgba(244,114,182,0.28)' },
    screwing: { color: '#FBBF24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.28)'  },
    walking:  { color: '#34D399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.28)'  },
  };
  return map[type?.toLowerCase()] || { color: 'var(--nd-tx35)', bg: 'var(--nd-w05)', border: 'var(--nd-w12)' };
};

// ── UserAvatar (logic preserved exactly) ──────────────────────────
function UserAvatar({ firstName, lastName, size = 44 }) {
  const gradientColors = React.useMemo(() => {
    const gradients = [
      ['#667eea', '#764ba2'],
      ['#f093fb', '#f5576c'],
      ['#4facfe', '#00f2fe'],
      ['#43e97b', '#38f9d7'],
      ['#fa709a', '#fee140'],
      ['#30cfd0', 'var(--nd-surface)']
    ];
    const name = `${firstName} ${lastName}`;
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return gradients[hash % gradients.length];
  }, [firstName, lastName]);

  const initials = React.useMemo(() => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  }, [firstName, lastName]);

  return (
    <div style={{
      width: size, height: size, minWidth: size,
      borderRadius: '10px',
      background: `linear-gradient(135deg, ${gradientColors[0]}, ${gradientColors[1]})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'white', fontWeight: '700', fontSize: size * 0.35,
      boxShadow: '0 3px 10px rgba(0,0,0,0.30)', letterSpacing: '0.5px',
      flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//   MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
const NovaaTracking = () => {
  const config = getAuthHeader();
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard]   = useState({
    totalDevices: 0, onlineDevices: 0, activeJobs: 0, totalDataPoints: 0
  });
  const [activityLog, setActivityLog]         = useState([]);
  const [activitySummary, setActivitySummary] = useState([]);
  const [viewMode, setViewMode]               = useState('log');
  const [selectedFO, setSelectedFO]           = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      console.log('Fetching tracker dashboard data...');
      const [dashboardRes, activityLogRes, activitySummaryRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/tracker/dashboard`,        config),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/tracker/activity-log`,     config),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/tracker/activity-summary`, config)
      ]);
      console.log('Dashboard data:',     dashboardRes.data);
      console.log('Activity log:',       activityLogRes.data);
      console.log('Activity summary:',   activitySummaryRes.data);
      setDashboard(dashboardRes.data);
      setActivityLog(activityLogRes.data);
      setActivitySummary(activitySummaryRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Dashboard Load Error:', error.response?.data || error);
      setLoading(false);
    } finally {
      if (showRefresh) setRefreshing(false);
    }
  };

  const handleRefresh = () => fetchDashboardData(true);

  const formatDuration = (minutes) => {
    if (!minutes || minutes < 0) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins  = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // ── Loading screen ────────────────────────────────────────────
  if (loading) return (
    <div style={{ background: T.bg, fontFamily: T.font, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <style>{STYLES}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 44, height: 44, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.blue}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 14px' }} />
        <div style={{ color: T.muted, fontSize: 13 }}>Loading NOVAA Tracker…</div>
      </div>
    </div>
  );

  const onlinePct = dashboard.totalDevices > 0
    ? Math.round((dashboard.onlineDevices / dashboard.totalDevices) * 100)
    : 0;

  // ── Stat card config ──────────────────────────────────────────
  const statCards = [
    {
      label: 'Total Devices',    value: dashboard.totalDevices,
      sub: 'Registered',         color: T.blue,
      Icon: Radio,               delay: 0,
    },
    {
      label: 'Online Now',       value: dashboard.onlineDevices,
      sub: `${onlinePct}% active`, color: T.green,
      Icon: Wifi,                delay: 0.06,
    },
    {
      label: 'Active Jobs',      value: dashboard.activeJobs,
      sub: 'Today',              color: T.amber,
      Icon: Activity,            delay: 0.12,
    },
    {
      label: 'Total Data Points',value: dashboard.totalDataPoints.toLocaleString(),
      sub: 'All time',           color: T.cyan,
      Icon: Database,            delay: 0.18,
    },
  ];

  return (
    <div style={{ background: T.bg, fontFamily: T.font, color: T.text, padding: '14px 20px' }}>
      <style>{STYLES}</style>

      {/* ── PAGE HEADER ─────────────────────────────────────────── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 18, overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
          <div>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 5 }}>NOVAA · IoT INTELLIGENCE</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>NOVAA Tracker</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>Real-time Workforce Intelligence &amp; Activity Monitoring</div>
          </div>

          {/* Device ratio + refresh */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 4 }}>Devices Online</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                <span style={{ fontSize: 32, fontWeight: 900, color: T.green, fontFamily: T.mono, lineHeight: 1 }}>
                  {dashboard.onlineDevices}
                </span>
                <span style={{ fontSize: 16, color: T.muted, fontFamily: T.mono }}>/{dashboard.totalDevices}</span>
              </div>
              {/* mini online bar */}
              <div style={{ marginTop: 5, height: 3, width: 80, background: 'var(--nd-w08)', borderRadius: 99, overflow: 'hidden', marginLeft: 'auto' }}>
                <div style={{ height: '100%', width: `${onlinePct}%`, background: `linear-gradient(90deg,${T.green},${T.teal})`, borderRadius: 99, transition: 'width 0.6s ease' }} />
              </div>
            </div>

            <button
              className="nt-refresh-btn"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw size={14} style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      {/* ── STAT CARDS ──────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14, marginBottom: 18 }}>
        {statCards.map((s) => (
          <div key={s.label} className="nt-stat-card" style={{
            background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
            padding: '18px 20px', boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            animationDelay: `${s.delay}s`, position: 'relative', overflow: 'hidden',
          }}>
            {/* corner accent */}
            <div style={{ position: 'absolute', top: 0, right: 0, width: 70, height: 70, borderRadius: '0 14px 0 70px', background: `${s.color}0C` }} />
            {/* accent bar left */}
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg,${s.color},transparent)`, borderRadius: '14px 0 0 14px' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase' }}>{s.label}</div>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: `${s.color}18`, border: `1px solid ${s.color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <s.Icon size={14} color={s.color} />
              </div>
            </div>
            <div style={{ fontSize: 34, fontWeight: 900, color: s.color, fontFamily: T.mono, letterSpacing: '-1px', lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 5 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── VIEW TOGGLE ─────────────────────────────────────────── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 6, marginBottom: 18, display: 'flex', gap: 6 }}>
        <button className={`nt-tab ${viewMode === 'log' ? 'nt-tab-active' : 'nt-tab-inactive'}`} onClick={() => setViewMode('log')}>
          <Activity size={15} /> Activity Log
        </button>
        <button className={`nt-tab ${viewMode === 'summary' ? 'nt-tab-active' : 'nt-tab-inactive'}`} onClick={() => setViewMode('summary')}>
          <BarChart3 size={15} /> FO Summary
        </button>
      </div>

      {/* ── ACTIVITY LOG TABLE ───────────────────────────────────── */}
      {viewMode === 'log' && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
          {/* section head */}
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Activity size={14} color={T.blue} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Activity Log</span>
            <span style={{ fontSize: 10, color: T.muted, fontFamily: T.mono, marginLeft: 2 }}>Last 7 Days</span>
          </div>

          {activityLog.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 52, marginBottom: 14 }}>📊</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>No activity found</div>
              <div style={{ color: T.muted, fontSize: 12 }}>Activity will appear here once FOs start working</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th className="nt-th">Field Operator</th>
                    <th className="nt-th">Activity Type</th>
                    <th className="nt-th">Client &amp; Site</th>
                    <th className="nt-th">Time Period</th>
                    <th className="nt-th">Duration</th>
                    <th className="nt-th nt-th-right">Data Points</th>
                  </tr>
                </thead>
                <tbody>
                  {activityLog.map((activity, index) => {
                    const ac = getActivityCfg(activity.activity_type);
                    return (
                      <tr
                        key={`${activity.user_id}-${activity.schedule_id}-${activity.activity_type}-${index}`}
                        className="nt-tr"
                      >
                        {/* Field Operator */}
                        <td className="nt-td">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <UserAvatar firstName={activity.first_name} lastName={activity.last_name} size={36} />
                            <div>
                              <div style={{ fontWeight: 600, color: T.text, fontSize: 13 }}>
                                {activity.first_name} {activity.last_name}
                              </div>
                              <div style={{ fontSize: 11, color: T.muted }}>{activity.department || 'Field Operator'}</div>
                            </div>
                          </div>
                        </td>

                        {/* Activity badge */}
                        <td className="nt-td">
                          <span style={{
                            background: ac.bg, color: ac.color, border: `1px solid ${ac.border}`,
                            padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                            textTransform: 'capitalize', letterSpacing: '0.06em', fontFamily: T.mono,
                          }}>
                            {activity.activity_type || 'Unknown'}
                          </span>
                        </td>

                        {/* Client & Site */}
                        <td className="nt-td">
                          {activity.client_name ? (
                            <div>
                              <div style={{ fontSize: 13, color: T.text, fontWeight: 600, marginBottom: 3 }}>{activity.client_name}</div>
                              <div style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <MapPin size={10} /> {activity.site_name}
                              </div>
                            </div>
                          ) : (
                            <div style={{ fontSize: 12, color: T.muted, fontStyle: 'italic' }}>No job assigned</div>
                          )}
                        </td>

                        {/* Time period */}
                        <td className="nt-td">
                          <div style={{ fontSize: 12, color: T.text, fontWeight: 600, fontFamily: T.mono, marginBottom: 3 }}>
                            {formatTime(activity.activity_start)} – {formatTime(activity.activity_end)}
                          </div>
                          <div style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Calendar size={10} />
                            {new Date(activity.scheduled_date || activity.activity_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        </td>

                        {/* Duration */}
                        <td className="nt-td">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 24, height: 24, borderRadius: 6, background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.20)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Timer size={11} color={T.amber} />
                            </div>
                            <span style={{ fontSize: 13, color: T.text, fontWeight: 700, fontFamily: T.mono }}>
                              {formatDuration(activity.duration_minutes)}
                            </span>
                          </div>
                        </td>

                        {/* Data points */}
                        <td className="nt-td-right">
                          <div style={{ fontSize: 14, color: T.teal, fontWeight: 800, fontFamily: T.mono }}>
                            {parseInt(activity.data_points).toLocaleString()}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── FO SUMMARY TABLE ────────────────────────────────────── */}
      {viewMode === 'summary' && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
          {/* section head */}
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(34,211,165,0.12)', border: '1px solid rgba(34,211,165,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BarChart3 size={14} color={T.teal} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Field Operator Performance Summary</span>
            <span style={{ fontSize: 10, color: T.muted, fontFamily: T.mono, marginLeft: 2 }}>Last 30 Days</span>
          </div>

          {activitySummary.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 52, marginBottom: 14 }}>👷</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>No FO data available</div>
              <div style={{ color: T.muted, fontSize: 12 }}>Summary will appear once field operators complete jobs</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th className="nt-th">Field Operator</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Jobs Completed</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Days Active</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Total Hours</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Activity Types</th>
                    <th className="nt-th nt-th-right">Total Data Points</th>
                  </tr>
                </thead>
                <tbody>
                  {activitySummary.map((fo) => (
                    <tr key={fo.user_id} className="nt-tr">
                      {/* FO name */}
                      <td className="nt-td">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                          <UserAvatar firstName={fo.first_name} lastName={fo.last_name} size={40} />
                          <div>
                            <div style={{ fontWeight: 700, color: T.text, fontSize: 13, marginBottom: 3 }}>
                              {fo.first_name} {fo.last_name}
                            </div>
                            <div style={{ fontSize: 11, color: T.muted }}>
                              Last active: <span style={{ fontFamily: T.mono }}>{formatTime(fo.last_activity)}</span>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Jobs Completed */}
                      <td className="nt-td" style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: T.blue, fontFamily: T.mono }}>{fo.total_jobs}</div>
                      </td>

                      {/* Days Active */}
                      <td className="nt-td" style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: T.green, fontFamily: T.mono }}>{fo.days_active}</div>
                      </td>

                      {/* Total Hours */}
                      <td className="nt-td" style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: T.amber, fontFamily: T.mono }}>{Math.round(fo.total_hours)}h</div>
                      </td>

                      {/* Activity Types */}
                      <td className="nt-td" style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: T.cyan, fontFamily: T.mono }}>{fo.activity_types}</div>
                      </td>

                      {/* Total Data Points */}
                      <td className="nt-td-right">
                        <div style={{ fontSize: 18, fontWeight: 800, color: T.teal, fontFamily: T.mono }}>
                          {parseInt(fo.total_data_points).toLocaleString()}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NovaaTracking;